<script setup lang="ts">
defineOptions({
  name: 'Alfred',
});
// 1. 类型定义
interface MenuItem {
  id: string
  icon: string
  label: string
  action?: () => void
}

interface Position {
  x: number
  y: number
}

// 2. 导入必要的组合式API
import { ref, reactive, onMounted, onUnmounted, computed, watch, type CSSProperties } from 'vue'
import { useRouter } from 'vue-router';
import { getRegistrationUrl } from '../utils/registration';

const router = useRouter();
// 3. 定义菜单项
const menuItems = reactive<MenuItem[]>([
  { id: 'signup', icon: '📝', label: '报名', action: () => { window.open(getRegistrationUrl(), '_blank') } },
  { id: 'home', icon: '🏠', label: '首页', action: () => { router.push('/') } },
  { id: 'schedule', icon: '📅', label: '大会议程', action: () => { router.push('/#schedule') } },
  { id: 'news', icon: '📰', label: '最新消息', action: () => { router.push('/journalism/index') } },
  { id: 'top', icon: '⬆️', label: '返回顶部', action: () => { window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }) } },
])

// 4. 核心状态管理
const isExpanded = ref(false)
const isDragging = ref(false)
const opacity = ref(1)
const isAttached = ref(false)
const attachedSide = ref<'left' | 'right' | null>(null)

// 5. 位置状态管理
const position = reactive<Position>({
  x: typeof window !== 'undefined' ? window.innerWidth - 80 : 0,
  y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
})

const startPos = reactive<Position>({ x: 0, y: 0 })
const touchStartTime = ref(0)
const touchStartPosition = reactive<Position>({ x: 0, y: 0 })
const isTouchMove = ref(false)

// 常量定义
const BUTTON_SIZE = 60
const PADDING = 10
const THRESHOLD = 50

// 6. 计算属性
const buttonStyle = computed((): CSSProperties => ({
  left: `${position.x}px`,
  top: `${position.y}px`,
  opacity: isExpanded.value ? 0 : opacity.value,
  visibility: isExpanded.value ? 'hidden' : 'visible',
  transform: isDragging.value
    ? 'scale(0.95)'
    : isAttached.value
      ? `${attachedSide.value === 'left' ? 'translateX(-25%)' : 'translateX(25%)'} scale(0.92)`
      : 'scale(1)',
  transition: isDragging.value
    ? 'none'
    : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  pointerEvents: isExpanded.value ? 'none' : 'auto',
}))

const menuPosition = computed((): CSSProperties => {
  const screenHeight = window.innerHeight
  const screenWidth = window.innerWidth
  const menuHeight = 218
  const menuWidth = 318

  const shouldShowAbove = position.y < screenHeight / 2

  const verticalPosition = shouldShowAbove
    ? position.y + 80
    : position.y - menuHeight - 20

  const horizontalPosition = (screenWidth - menuWidth) / 2

  return {
    position: 'fixed',
    top: `${verticalPosition}px`,
    left: `${horizontalPosition}px`,
    transformOrigin: 'center',
  }
})

// 7. 动画状态
const isAnimating = ref(false)
const animationTimeout = ref<number | null>(null)

// 8. 方法定义
const handleClick = (e: MouseEvent | TouchEvent) => {
  e.preventDefault()
  e.stopPropagation()
  if (!isDragging.value && !isAnimating.value) {
    toggleExpand()
  }
}

// 事件监听器管理
const manageEventListeners = (evt: Event) => {
  if (evt instanceof MouseEvent) {
    const moveEvent = 'mousemove'
    const endEvent = 'mouseup'
    const method = 'addEventListener'
    document[method](moveEvent, handleDrag, { passive: false })
    document[method](endEvent, stopDrag)
  } else if (evt instanceof TouchEvent) {
    const moveEvent = 'touchmove'
    const endEvent = 'touchend'
    const method = 'addEventListener'
    document[method](moveEvent, handleDrag, { passive: false })
    document[method](endEvent, stopDrag)
  }
}

// 处理拖动开始
const startDrag = (e: MouseEvent | TouchEvent) => {
  if (isExpanded.value) { return }

  const isTouchEvent = 'touches' in e
  const pos = isTouchEvent ? e.touches[0] : e

  if (isTouchEvent) {
    touchStartTime.value = Date.now()
    touchStartPosition.x = pos.clientX
    touchStartPosition.y = pos.clientY
    isTouchMove.value = false
  }

  isDragging.value = true
  opacity.value = 0.6
  isAttached.value = false
  attachedSide.value = null

  startPos.x = pos.clientX - position.x
  startPos.y = pos.clientY - position.y

  manageEventListeners(e)
}

// 使用 requestAnimationFrame 优化拖动
let animationFrameId: number | null = null
const handleDrag = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value) { return }

  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }

  animationFrameId = requestAnimationFrame(() => {
    const isTouchEvent = 'touches' in e
    const pos = isTouchEvent ? e.touches[0] : e

    position.x = pos.clientX - startPos.x
    position.y = pos.clientY - startPos.y

    if (isTouchEvent) {
      const dx = pos.clientX - touchStartPosition.x
      const dy = pos.clientY - touchStartPosition.y
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        isTouchMove.value = true
      }
    }
  })
}

// 停止拖动
const stopDrag = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value) { return }

  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }

  const isTouchEvent = 'touches' in e
  if (isTouchEvent) {
    const touchEndTime = Date.now()
    const touchDuration = touchEndTime - touchStartTime.value

    if (touchDuration < 200 && !isTouchMove.value) {
      isDragging.value = false
      handleClick(e)
      manageEventListeners(e)
      return
    }
  }

  isDragging.value = false
  opacity.value = 1

  const screenWidth = window.innerWidth

  const clampedX = Math.max(0, Math.min(position.x, screenWidth - BUTTON_SIZE))
  const clampedY = Math.max(0, Math.min(position.y, window.innerHeight - BUTTON_SIZE))
  position.x = clampedX < THRESHOLD ? 0 : (clampedX > screenWidth - THRESHOLD - BUTTON_SIZE ? screenWidth - BUTTON_SIZE + PADDING : clampedX)
  position.y = clampedY
  isAttached.value = position.x === 0 || position.x === screenWidth - BUTTON_SIZE + PADDING
  attachedSide.value = position.x === 0 ? 'left' : (position.x === screenWidth - BUTTON_SIZE + PADDING ? 'right' : null)

  manageEventListeners(e)
}

const toggleExpand = () => {
  if (isDragging.value || isAnimating.value) { return }

  isAnimating.value = true
  isExpanded.value = !isExpanded.value

  if (isExpanded.value) {
    isDragging.value = false
    isAttached.value = false
  }

  if (animationTimeout.value) {
    clearTimeout(animationTimeout.value)
  }

  animationTimeout.value = window.setTimeout(() => {
    isAnimating.value = false
  }, 300)
}
// 监听点击事件
const handleClickOutside = (event: MouseEvent) => {
  if (isExpanded.value) {
    const target = event.target as HTMLElement
    if (!target.closest('.alfred-menu')) {
      toggleExpand()
    }
  }
}

watch(isExpanded, (newValue) => {
  if (newValue) {
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// 9. 生命周期钩子
onMounted(() => {
  const handleResize = () => {
    position.x = Math.min(position.x, window.innerWidth - 70)
    position.y = Math.min(position.y, window.innerHeight - 70)

    if (isAttached.value) {
      if (attachedSide.value === 'right') {
        position.x = window.innerWidth - 45
      } else if (attachedSide.value === 'left') {
        position.x = 0
      }
    }
  }

  window.addEventListener('resize', handleResize)

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
    manageEventListeners(new Event('remove'))
    if (animationTimeout.value) {
      clearTimeout(animationTimeout.value)
    }
  })
})
</script>

<template>
  <div>
    <!-- 主按钮 -->
    <div
      class="fixed z-[9999] touch-none select-none"
      :style="buttonStyle"
      @mousedown.prevent="startDrag"
      @touchstart.prevent="startDrag"
      @click.prevent="handleClick"
    >
      <div
        class="relative"
        :class="{
          'pointer-events-none': isAnimating,
          'opacity-90': isAttached
        }"
      >
        <div
          class="h-[48px] w-[48px] flex cursor-pointer items-center justify-center border border-blue-300/30 rounded-full from-blue-400/90 to-blue-500/90 bg-gradient-to-b shadow-blue-400/20 shadow-lg backdrop-blur-xl transition-all duration-300 ease-out"
          :class="{
            'scale-95 from-blue-400/80 to-blue-500/80': isAttached,
            'hover:scale-105 hover:from-blue-300/90 hover:to-blue-400/90': !isAttached && !isDragging,
            'active:scale-90': !isDragging
          }"
        >
          <div
            class="h-[32px] w-[32px] flex items-center justify-center rounded-full from-white to-blue-50 bg-gradient-to-b shadow-inner transition-all duration-300"
            :class="{
              'scale-95 from-blue-50 to-white': isAttached
            }"
          >
            <div
              class="h-[16px] w-[16px] rounded-[3px] bg-blue-500 transition-all duration-300"
              :class="{
                'rotate-45 scale-90 bg-blue-400': isExpanded,
                'scale-90': isAttached
              }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 展开菜单 -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="isExpanded"
        :class="[
          'z-[9999] grid grid-cols-3 gap-3 p-3',
          'rounded-2xl bg-gradient-to-b from-#37fffc to-#003cff',
          'backdrop-blur-xl border border-blue-400/20',
          'shadow-xl shadow-blue-500/10',
        ]"
        :style="menuPosition"
      >
        <button
          v-for="item in menuItems"
          :key="item.id"
          class="h-[88px] w-[88px] flex flex-col items-center justify-center gap-1 border border-blue-100/50 rounded-full from-white to-blue-50 bg-gradient-to-b transition-all duration-200 active:scale-90 hover:from-blue-50 hover:to-white hover:shadow-blue-500/10 hover:shadow-md"
          @click="() => {
            item.action?.()
            toggleExpand()
          }"
        >
          <span class="text-[24px]">{{ item.icon }}</span>
          <span class="text-[14px] text-blue-600/70">{{ item.label }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>
